/* =========================================================
   ALPENGRAVUR — KONFIGURATIONSWERKSTATT
   Standalone multi-product configurator page. Reuses the shared 3D
   engine from product-3d.js (same building blocks the product pages
   used inline before); this file owns the picker/step/cart UI and
   real Shopify product/variant/cart wiring.
========================================================== */
import * as THREE from 'three';
import {
  CONFIGS, buildProduct, setModelUrlResolver, attachSpin, FONTS, FONT_CATS, fontById,
  EngraveStamp, otsuThreshold, photoToSilhouette, supportsWebGL
} from './product-3d.js';

(function(){
  "use strict";

  var DRAFT_KEY = 'agWorkshopDraft';
  var STEP_ORDER = ['product', 'variant', 'design', 'position', 'cart'];
  var STEP_LABELS = { product: 'Produkt', variant: 'Material', design: 'Text/Foto', position: 'Position', cart: 'Warenkorb' };

  var root = document.querySelector('[data-workshop]');
  if(!root) return;

  var picker = document.getElementById('WorkshopPicker');
  var pickerGrid = document.getElementById('WorkshopPickerGrid');
  var shell = document.getElementById('WorkshopShell');
  var controlsEl = document.getElementById('WorkshopControls');
  var stepsNav = document.getElementById('WorkshopSteps');
  var stageEl = document.getElementById('WorkshopStage');
  var canvasHost = stageEl.querySelector('[data-canvas-host]');
  var fallbackNote = stageEl.querySelector('[data-fallback-note]');
  var resetViewBtn = stageEl.querySelector('[data-reset-view]');
  var placeholderBadge = stageEl.querySelector('[data-placeholder-badge]');
  /* a merchant-approved, launch-enabled product must never show this
     internal QA signal to real customers, even if its EngraveSurface
     interface isn't wired up yet -- the merchant already looked at it and
     said it's fine. Only a not-yet-launched product (dev/preview use only)
     shows it. */
  function updatePlaceholderBadge(isPlaceholder){
    var show = isPlaceholder && !(state.product && state.product.enabled);
    if(placeholderBadge) placeholderBadge.hidden = !show;
  }

  /* ---------------- "coming soon" gate for not-yet-launched products ---------------- */
  var soonOverlay = document.getElementById('WorkshopSoonOverlay');
  var soonClose = document.getElementById('WorkshopSoonClose');
  var soonOk = document.getElementById('WorkshopSoonOk');
  function showSoonOverlay(){ if(soonOverlay) soonOverlay.hidden = false; }
  function hideSoonOverlay(){ if(soonOverlay) soonOverlay.hidden = true; }
  if(soonClose) soonClose.addEventListener('click', hideSoonOverlay);
  if(soonOk) soonOk.addEventListener('click', hideSoonOverlay);
  if(soonOverlay) soonOverlay.addEventListener('click', function(e){ if(e.target === soonOverlay) hideSoonOverlay(); });
  var productTagThumb = document.getElementById('WorkshopProductThumb');
  var productTagTitle = document.getElementById('WorkshopProductTitle');
  var productTagVariant = document.getElementById('WorkshopVariantTitle');
  var swapProductBtn = document.getElementById('WorkshopSwapProduct');
  var priceEl = document.getElementById('WorkshopPrice');
  var addToCartBtn = document.getElementById('WorkshopAddToCart');
  var addToCartLabel = document.getElementById('WorkshopAddToCartLabel');
  var formError = document.getElementById('WorkshopFormError');

  var webglOk = supportsWebGL();

  /* ---------------- product data ---------------- */
  var productsScript = document.getElementById('WorkshopProducts');
  var allProducts = [];
  try { allProducts = JSON.parse(productsScript.textContent) || []; } catch(e){ allProducts = []; }
  allProducts = allProducts.filter(function(p){ return p && CONFIGS[p.handle]; });

  /* Shopify fingerprints asset filenames (asset_url), so the 3D engine
     can't just guess "<handle>.glb" itself — the real, fingerprinted URLs
     are emitted server-side per handle and wired in here once at boot. */
  var modelUrlsScript = document.getElementById('WorkshopModelUrls');
  var modelUrls = {};
  try { modelUrls = JSON.parse(modelUrlsScript.textContent) || {}; } catch(e){ modelUrls = {}; }
  setModelUrlResolver(function(handle){ return modelUrls[handle] || (handle + '.glb'); });

  function findProduct(handle){ return allProducts.filter(function(p){ return p.handle === handle; })[0] || null; }
  function findVariant(product, variantId){
    if(!product) return null;
    variantId = String(variantId);
    return product.variants.filter(function(v){ return String(v.id) === variantId; })[0] || product.variants[0] || null;
  }

  /* generic, invented example texts (no real/identifiable people) shown as a
     starting point so the preview doesn't always read "Familie Gruber" */
  var EXAMPLE_TEXTS = [
    'Familie Gruber', 'Familie Hofer', 'Familie Steiner', 'Familie Wimmer',
    'Familie Berger', 'Familie Wagner', 'Familie Huber', 'Familie Moser',
    'Anna & Tom', 'Lena & Paul', 'Sophie & Max', 'Marie & Jonas',
    'Für immer Dein', 'Mit Liebe gemacht', 'Danke Mama', 'Bester Papa',
    'Opa Franz', 'Oma Rosa', 'Unser Zuhause', 'Herzlich Willkommen'
  ];
  function randomExampleText(){ return EXAMPLE_TEXTS[Math.floor(Math.random() * EXAMPLE_TEXTS.length)]; }

  /* ---------------- state ---------------- */
  var state = {
    product: null, variant: null, surface: null,
    mode: 'text', text: randomExampleText(), font: 'fraunces',
    rot: 0, size: 0, sizeAuto: true, lineHeight: 1.15, align: 'center',
    imgFile: null, imgFileName: null, imgScale: 1,
    pos: { x: 0.5, y: 0.5 },
    step: 'product'
  };

  function money(cents){
    if(window.AG && typeof window.AG.formatMoney === 'function') return window.AG.formatMoney(cents);
    return '€' + (cents/100).toFixed(2).replace('.', ',');
  }

  /* ---------------- draft persistence (text config only — never the photo file) ---------------- */
  function saveDraft(){
    try {
      if(!state.product) return;
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({
        handle: state.product.handle, variantId: state.variant ? state.variant.id : null,
        surface: state.surface, mode: state.mode, text: state.text, font: state.font,
        rot: state.rot, size: state.size, sizeAuto: state.sizeAuto, lineHeight: state.lineHeight,
        align: state.align, imgScale: state.imgScale, pos: state.pos
      }));
    } catch(e){ /* private browsing / storage disabled — draft is a convenience, not required */ }
  }
  function loadDraft(){
    try { return JSON.parse(sessionStorage.getItem(DRAFT_KEY) || 'null'); } catch(e){ return null; }
  }

  /* ================= 3D viewer (per selected product+surface) ================= */
  var scene, camera, renderer, spin, stamp, decal, product3d, guideGroup, safeOutline, guideV, guideH, raycaster, moving = false, placeInfo, topY, decalMat, viewerRO;
  var canvasHostListeners = null;
  var currentModel = null; /* the loaded+built THREE.Object3D for the mounted product, holds userData.engraveSurfaces */
  var surfaceEnvelope = null; /* placement envelope for the active surface — see computeSurfaceEnvelope() */

  function cfgFor(){ return CONFIGS[state.product.handle]; }
  function engraveCfg(){
    var cfg = cfgFor();
    return cfg.engraveSurfaces ? cfg.engraveSurfaces[state.surface] : cfg.engrave;
  }
  /* the placement envelope (how big/where the draggable area is) prefers the
     model's own auto-detected EngraveSurface node (real delivered geometry);
     when the mounted GLB doesn't have one (today's placeholder models) this
     falls back to the same hand-authored CONFIGS.dims numbers the engine
     always used, so nothing already working changes behavior. */
  function computeSurfaceEnvelope(){
    var cfg = cfgFor(), ecfg = engraveCfg();
    var key = cfg.engraveSurfaces ? state.surface : 'default';
    var auto = currentModel && currentModel.userData.engraveSurfaces && currentModel.userData.engraveSurfaces[key];
    if(auto) return auto;
    if(ecfg.face === 'cylinder'){
      return { face:'cylinder', r: cfg.dims.r, minY: 0, maxY: cfg.dims.h };
    }
    if(ecfg.face === 'front'){
      return { face:'front', w: cfg.dims.frontW, h: cfg.dims.frontH, cx: 0, fcy: cfg.dims.frontCY, fz: cfg.dims.frontZ };
    }
    return { face:'top', w: (cfg.dims.w || cfg.dims.r*2 || 10), d: (cfg.dims.d || cfg.dims.r*2 || 10), cx: 0, cz: 0, y: topY };
  }
  /* EngraveStamp only understands a flat w/h (cm) canvas — a cylinder face
     describes itself as an arc (angW degrees + hgt) instead, so translate
     that into an equivalent flat w/h (arc length at the product's radius x
     the same height) before handing it to the stamp. */
  function stampDims(ecfg, env){
    if(ecfg.face === 'cylinder'){
      var r = env ? env.r : cfgFor().dims.r;
      var angW = env ? env.angW : ecfg.angW;
      return { w: THREE.MathUtils.degToRad(angW) * r, h: ecfg.hgt };
    }
    return { w: ecfg.w, h: ecfg.h };
  }

  function placeDecalFlat(ecfg, env, nx, ny){
    var w = ecfg.w, h = ecfg.h;
    var y = env.y, cx = env.cx || 0, cz = env.cz || 0;
    decal.geometry.dispose();
    decal.geometry = new THREE.PlaneGeometry(w, h);
    decal.rotation.set(-Math.PI/2, 0, 0);
    var rangeX = (env.w*0.86 - w)/2, rangeZ = (env.d*0.86 - h)/2;
    decal.position.set(cx + (nx-0.5)*2*rangeX, y+0.06, cz + (ny-0.5)*2*rangeZ);
    var ow = env.w*0.86, oh = env.d*0.86;
    var pts = [ new THREE.Vector3(cx-ow/2,y+0.03,cz-oh/2), new THREE.Vector3(cx+ow/2,y+0.03,cz-oh/2), new THREE.Vector3(cx+ow/2,y+0.03,cz+oh/2), new THREE.Vector3(cx-ow/2,y+0.03,cz+oh/2) ];
    safeOutline.geometry.dispose(); safeOutline.geometry = new THREE.BufferGeometry().setFromPoints(pts);
    guideV.geometry.dispose(); guideV.geometry = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(cx,y+0.04,cz-oh/2), new THREE.Vector3(cx,y+0.04,cz+oh/2)]);
    guideH.geometry.dispose(); guideH.geometry = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(cx-ow/2,y+0.04,cz), new THREE.Vector3(cx+ow/2,y+0.04,cz)]);
    return { rangeX: rangeX, rangeZ: rangeZ, cx: cx, cz: cz, y: y };
  }
  /* a vertical face (e.g. the Schnapsbank's backrest) — same idea as
     placeDecalFlat but the decal plane faces +Z instead of +Y, and the
     free axes are X (left-right) and Y (up-down) instead of X/Z. The
     panel's own size/position comes from cfg.dims.front* fields (set only
     on products that actually have a front face) rather than the generic
     dims.w/d used for 'top', since a front panel's own width/height is
     usually smaller than the product's full footprint. */
  function placeDecalFront(ecfg, env, nx, ny){
    var w = ecfg.w, h = ecfg.h;
    var fw = env.w, fh = env.h, fz = env.fz, fcy = env.fcy, cx0 = env.cx || 0;
    decal.geometry.dispose();
    decal.geometry = new THREE.PlaneGeometry(w, h);
    decal.rotation.set(0, Math.PI, 0);
    var rangeX = (fw*0.86 - w)/2, rangeY = (fh*0.86 - h)/2;
    var cx = cx0 + (nx-0.5)*2*rangeX, cy = fcy + (ny-0.5)*2*rangeY;
    decal.position.set(cx, cy, fz + 0.03);
    var ow = fw*0.86, oh = fh*0.86;
    var pts = [
      new THREE.Vector3(cx0-ow/2, fcy-oh/2, fz+0.02), new THREE.Vector3(cx0+ow/2, fcy-oh/2, fz+0.02),
      new THREE.Vector3(cx0+ow/2, fcy+oh/2, fz+0.02), new THREE.Vector3(cx0-ow/2, fcy+oh/2, fz+0.02)
    ];
    safeOutline.geometry.dispose(); safeOutline.geometry = new THREE.BufferGeometry().setFromPoints(pts);
    guideV.geometry.dispose(); guideV.geometry = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(cx0,fcy-oh/2,fz+0.025), new THREE.Vector3(cx0,fcy+oh/2,fz+0.025)]);
    guideH.geometry.dispose(); guideH.geometry = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(cx0-ow/2,fcy,fz+0.025), new THREE.Vector3(cx0+ow/2,fcy,fz+0.025)]);
    return { rangeX: rangeX, rangeY: rangeY, fcy: fcy, fz: fz, cx: cx0 };
  }
  function placeDecalCylinder(ecfg, env, nx, ny){
    var r = env.r + 0.03;
    var angW = THREE.MathUtils.degToRad(env.angW);
    var hgt = ecfg.hgt;
    decal.geometry.dispose();
    decal.geometry = new THREE.CylinderGeometry(r, r, hgt, 32, 1, true, -angW/2, angW);
    var maxAng = THREE.MathUtils.degToRad(env.angW*1.15);
    var minY = env.minY, maxY = env.maxY - hgt;
    var theta = (nx-0.5) * (maxAng - angW);
    var y = minY + ny*(maxY-minY);
    decal.rotation.set(0, theta, 0);
    decal.position.set(0, y + hgt/2, 0);
    safeOutline.geometry.dispose(); safeOutline.geometry = new THREE.BufferGeometry();
    guideV.geometry.dispose(); guideV.geometry = new THREE.BufferGeometry();
    guideH.geometry.dispose(); guideH.geometry = new THREE.BufferGeometry();
    return { maxAng: maxAng, angW: angW, minY: minY, maxY: maxY, r: r };
  }
  function replaceDecal(nx, ny){
    var ecfg = engraveCfg();
    surfaceEnvelope = computeSurfaceEnvelope();
    if(surfaceEnvelope.face === 'cylinder') placeInfo = placeDecalCylinder(ecfg, surfaceEnvelope, nx, ny);
    else if(surfaceEnvelope.face === 'front') placeInfo = placeDecalFront(ecfg, surfaceEnvelope, nx, ny);
    else placeInfo = placeDecalFlat(ecfg, surfaceEnvelope, nx, ny);
  }

  function teardownViewer(){
    if(spin) spin.destroy();
    if(viewerRO) viewerRO.disconnect();
    if(renderer){ renderer.dispose(); }
    if(canvasHostListeners){
      canvasHost.removeEventListener('pointerdown', canvasHostListeners.down);
      canvasHost.removeEventListener('pointermove', canvasHostListeners.move);
      canvasHost.removeEventListener('pointerup', canvasHostListeners.up);
      canvasHost.removeEventListener('pointercancel', canvasHostListeners.up);
      canvasHostListeners = null;
    }
    moving = false;
    canvasHost.innerHTML = '';
    currentModel = null;
    surfaceEnvelope = null;
  }

  var mountGeneration = 0;

  function mountViewer(){
    teardownViewer();
    if(!webglOk){ fallbackNote.hidden = false; resetViewBtn.hidden = true; return; }
    fallbackNote.hidden = true; resetViewBtn.hidden = false;

    var myGeneration = ++mountGeneration;
    var loadingEl = el('div', 'workshop-model-loading', 'Modell wird geladen…');
    canvasHost.appendChild(loadingEl);

    var cfg = cfgFor();
    var handle = state.product.handle;
    buildProduct(cfg, handle).then(function(builtMesh){
      /* the user may have switched product/surface again while this GLB
         was still loading — a stale response landing after a newer mount
         must not overwrite the viewer the user is already looking at */
      if(myGeneration !== mountGeneration) return;
      if(loadingEl.parentNode) loadingEl.parentNode.removeChild(loadingEl);
      finishMountViewer(cfg, builtMesh);
    }).catch(function(err){
      if(myGeneration !== mountGeneration) return;
      if(window.console) console.error('[workshop] model load failed', handle, err);
      if(loadingEl.parentNode) loadingEl.parentNode.removeChild(loadingEl);
      fallbackNote.hidden = false; resetViewBtn.hidden = true;
    });
  }

  function finishMountViewer(cfg, builtMesh){
    scene = new THREE.Scene();
    product3d = new THREE.Group();
    product3d.add(builtMesh);
    scene.add(product3d);
    currentModel = builtMesh;
    updatePlaceholderBadge(!!(builtMesh.userData && builtMesh.userData.isPlaceholder));

    var bbox = new THREE.Box3().setFromObject(product3d);
    /* the bottle's swing-top bail arcs above the cap, so the group's own
       bounding box no longer equals the flat engrave surface's height */
    topY = (builtMesh.userData && typeof builtMesh.userData.flatTopY === 'number') ? builtMesh.userData.flatTopY : bbox.max.y;
    var size = new THREE.Vector3(); bbox.getSize(size);
    var footprint = Math.max(size.x, size.y, size.z);
    var sphere = new THREE.Sphere(); bbox.getBoundingSphere(sphere);
    var radius = sphere.radius;

    stamp = new EngraveStamp(stampDims(engraveCfg(), computeSurfaceEnvelope()));
    applyStampFromState();

    decalMat = new THREE.MeshBasicMaterial({ map: stamp.texture, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -4, side: THREE.DoubleSide });
    decal = new THREE.Mesh(new THREE.PlaneGeometry(1,1), decalMat);
    product3d.add(decal);

    guideGroup = new THREE.Group(); product3d.add(guideGroup);
    safeOutline = new THREE.LineLoop(new THREE.BufferGeometry(), new THREE.LineBasicMaterial({ color: 0xC98A46, transparent: true, opacity: 0.55 }));
    guideGroup.add(safeOutline);
    var guideMat = new THREE.LineBasicMaterial({ color: 0xC98A46, transparent: true, opacity: 0 });
    guideV = new THREE.Line(new THREE.BufferGeometry(), guideMat.clone());
    guideH = new THREE.Line(new THREE.BufferGeometry(), guideMat.clone());
    guideGroup.add(guideV); guideGroup.add(guideH);
    guideGroup.visible = false;

    replaceDecal(state.pos.x, state.pos.y);

    var key = new THREE.DirectionalLight(0xfff3e2, 0.9); key.position.set(footprint*1.4, footprint*2.2, footprint*1.6); scene.add(key);
    var fill = new THREE.DirectionalLight(0xd9e6f5, 0.3); fill.position.set(-footprint*1.8, footprint*1.2, -footprint*0.6); scene.add(fill);
    scene.add(new THREE.AmbientLight(0xffffff, 0.22));

    var shadowMesh = new THREE.Mesh(new THREE.CircleGeometry(footprint*1.35, 56), new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.1 }));
    shadowMesh.rotation.x = -Math.PI/2; shadowMesh.position.y = 0.01; scene.add(shadowMesh);

    camera = new THREE.PerspectiveCamera(34, 1, 0.1, 1000);
    var camDist = (radius / Math.sin(THREE.MathUtils.degToRad(17))) * 1.06;
    var elev = THREE.MathUtils.degToRad(48);
    camera.position.set(0, sphere.center.y + camDist*Math.sin(elev), camDist*Math.cos(elev));
    camera.lookAt(0, sphere.center.y, 0);

    renderer = new THREE.WebGLRenderer({ antialias: true });
    /* phones: 1.5x is visibly as sharp as 2-3x on a small stage, but far lighter on the GPU */
    var touchDevice = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, touchDevice ? 1.5 : 2));
    canvasHost.appendChild(renderer.domElement);

    var env = buildLocalStudioEnv(renderer);
    scene.background = env.background; scene.environment = env.environment;

    raycaster = new THREE.Raycaster();

    function pointerNDC(e){
      var rect = canvasHost.getBoundingClientRect();
      return new THREE.Vector2(((e.clientX-rect.left)/rect.width)*2-1, -((e.clientY-rect.top)/rect.height)*2+1);
    }
    var decalCenterW = new THREE.Vector3();
    function hitsDecal(e){
      decal.getWorldPosition(decalCenterW);
      var proj = decalCenterW.clone().project(camera);
      var rect = canvasHost.getBoundingClientRect();
      var sx = (proj.x*0.5+0.5)*rect.width + rect.left;
      var sy = (1-(proj.y*0.5+0.5))*rect.height + rect.top;
      var dx = e.clientX-sx, dy = e.clientY-sy;
      var thresh = Math.max(46, rect.width*0.13);
      return (dx*dx+dy*dy) < thresh*thresh;
    }
    spin = attachSpin(canvasHost, product3d, hitsDecal);

    function localRay(){
      var inv = new THREE.Matrix4().copy(product3d.matrixWorld).invert();
      var ray = raycaster.ray.clone();
      ray.applyMatrix4(inv);
      return ray;
    }
    function dragMove(e){
      raycaster.setFromCamera(pointerNDC(e), camera);
      var env = surfaceEnvelope || computeSurfaceEnvelope();
      var ray = localRay();
      if(env.face === 'cylinder'){
        var r = env.r;
        var ox=ray.origin.x, oz=ray.origin.z, dx=ray.direction.x, dz=ray.direction.z;
        var a = dx*dx+dz*dz, b = 2*(ox*dx+oz*dz), c = ox*ox+oz*oz-r*r;
        var disc = b*b-4*a*c;
        if(disc < 0 || a < 1e-6) return;
        var tt = (-b - Math.sqrt(disc))/(2*a);
        if(tt < 0) tt = (-b + Math.sqrt(disc))/(2*a);
        var hit = ray.origin.clone().addScaledVector(ray.direction, tt);
        var theta = Math.atan2(hit.x, hit.z);
        var nx = 0.5 + theta/(placeInfo.maxAng - placeInfo.angW || 1);
        var ny = (hit.y - placeInfo.minY)/(placeInfo.maxY-placeInfo.minY || 1);
        state.pos.x = Math.max(0, Math.min(1, nx));
        state.pos.y = Math.max(0.02, Math.min(0.98, ny));
      } else if(env.face === 'front'){
        var fz = env.fz, fcy = env.fcy, cx0 = env.cx || 0;
        var plane2 = new THREE.Plane(new THREE.Vector3(0,0,1), -fz);
        var hitPt2 = new THREE.Vector3();
        if(!ray.intersectPlane(plane2, hitPt2)) return;
        var fw = env.w*0.86, fh = env.h*0.86;
        var nx3 = 0.5 + (hitPt2.x-cx0)/fw, ny3 = 0.5 + (hitPt2.y-fcy)/fh;
        var snap2 = 0.018;
        if(Math.abs(nx3-0.5) < snap2){ nx3 = 0.5; guideV.material.opacity = 0.85; } else { guideV.material.opacity = 0; }
        if(Math.abs(ny3-0.5) < snap2){ ny3 = 0.5; guideH.material.opacity = 0.85; } else { guideH.material.opacity = 0; }
        state.pos.x = Math.max(0.03, Math.min(0.97, nx3));
        state.pos.y = Math.max(0.03, Math.min(0.97, ny3));
      } else {
        var topPlaneY = env.y, cx1 = env.cx || 0, cz1 = env.cz || 0;
        var plane = new THREE.Plane(new THREE.Vector3(0,1,0), -topPlaneY);
        var hitPt = new THREE.Vector3();
        if(!ray.intersectPlane(plane, hitPt)) return;
        var maxW = env.w*0.86, maxD = env.d*0.86;
        var nx2 = 0.5 + (hitPt.x-cx1)/maxW, ny2 = 0.5 + (hitPt.z-cz1)/maxD;
        var snap = 0.018;
        if(Math.abs(nx2-0.5) < snap){ nx2 = 0.5; guideV.material.opacity = 0.85; } else { guideV.material.opacity = 0; }
        if(Math.abs(ny2-0.5) < snap){ ny2 = 0.5; guideH.material.opacity = 0.85; } else { guideH.material.opacity = 0; }
        state.pos.x = Math.max(0.03, Math.min(0.97, nx2));
        state.pos.y = Math.max(0.03, Math.min(0.97, ny2));
      }
      replaceDecal(state.pos.x, state.pos.y);
      saveDraft();
    }
    function onDown(e){
      if(hitsDecal(e)){ moving = true; canvasHost.setPointerCapture(e.pointerId); guideGroup.visible = true; }
    }
    function onMove(e){ if(moving) dragMove(e); }
    function endMove(){ if(moving){ moving = false; guideV.material.opacity = 0; guideH.material.opacity = 0; } }
    canvasHost.addEventListener('pointerdown', onDown);
    canvasHost.addEventListener('pointermove', onMove);
    canvasHost.addEventListener('pointerup', endMove);
    canvasHost.addEventListener('pointercancel', endMove);
    canvasHostListeners = { down: onDown, move: onMove, up: endMove };

    function resize(){
      var w = stageEl.clientWidth, h = stageEl.clientHeight;
      if(!w || !h) return;
      camera.aspect = w/h; camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    }
    viewerRO = new ResizeObserver(resize);
    viewerRO.observe(stageEl);
    resize();

    /* Render loop that behaves on phones: paused while the tab is hidden or the viewer is scrolled
       out of view, and ~30 fps on touch devices when nobody has touched it for 2 s. */
    var stopped = false, rafId = 0, onScreen = true, lastFrame = 0, lastTouch = performance.now();
    var coarse = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
    function animate(ts){
      rafId = 0;
      if(stopped || document.hidden || !onScreen) return;
      rafId = requestAnimationFrame(animate);
      if(coarse && ts - lastTouch > 2000 && ts - lastFrame < 30) return;
      lastFrame = ts;
      spin.tick(); renderer.render(scene, camera);
    }
    function kick(){ if(!rafId && !stopped && onScreen && !document.hidden) rafId = requestAnimationFrame(animate); }
    function wake(){ lastTouch = performance.now(); kick(); }
    canvasHost.addEventListener('pointerdown', wake);
    canvasHost.addEventListener('pointermove', wake);
    document.addEventListener('visibilitychange', kick);
    var viewerIO = null;
    if('IntersectionObserver' in window){
      viewerIO = new IntersectionObserver(function(entries){ onScreen = entries[entries.length-1].isIntersecting; kick(); });
      viewerIO.observe(stageEl);
    }
    kick();
    mountViewer._stop = function(){
      stopped = true;
      document.removeEventListener('visibilitychange', kick);
      if(viewerIO) viewerIO.disconnect();
    };
  }

  function buildLocalStudioEnv(renderer){
    var w = 512, h = 256;

    /* what's literally visible behind the product — must stay a plain,
       smooth studio backdrop. This used to be the SAME canvas as the
       reflection map below (including its bright "softbox" streaks), which
       an equirectangular background projects as a harsh, glitchy-looking
       diagonal bright band once the camera isn't looking straight at it —
       exactly the "is this a joke" screenshot the merchant sent back. */
    var bgCanvas = document.createElement('canvas'); bgCanvas.width = w; bgCanvas.height = h;
    var bctx = bgCanvas.getContext('2d');
    var bgGrad = bctx.createLinearGradient(0,0,0,h);
    bgGrad.addColorStop(0, '#F7F3EC'); bgGrad.addColorStop(0.55, '#E9E3D4'); bgGrad.addColorStop(1, '#D2CAB7');
    bctx.fillStyle = bgGrad; bctx.fillRect(0,0,w,h);
    var bgTex = new THREE.CanvasTexture(bgCanvas);
    bgTex.mapping = THREE.EquirectangularReflectionMapping; bgTex.colorSpace = THREE.SRGBColorSpace;

    /* a separate, richer lighting map — including the soft "softbox window"
       streaks — used ONLY to light PBR reflections (metal/clearcoat), never
       rendered directly as the visible background */
    var envCanvas = document.createElement('canvas'); envCanvas.width = w; envCanvas.height = h;
    var ectx = envCanvas.getContext('2d');
    var g = ectx.createLinearGradient(0,0,0,h);
    g.addColorStop(0, '#F7F3EC'); g.addColorStop(0.42, '#DDD5C4'); g.addColorStop(0.58, '#B7AF9C'); g.addColorStop(1, '#8C8577');
    ectx.fillStyle = g; ectx.fillRect(0,0,w,h);
    var bandY = h*0.30, bandH = h*0.18;
    var bg = ectx.createLinearGradient(0,bandY,0,bandY+bandH);
    bg.addColorStop(0, 'rgba(255,252,244,0)'); bg.addColorStop(0.5, 'rgba(255,252,244,0.95)'); bg.addColorStop(1, 'rgba(255,252,244,0)');
    ectx.fillStyle = bg; ectx.fillRect(0,bandY,w,bandH);
    [w*0.28, w*0.74].forEach(function(cx){
      var vg = ectx.createLinearGradient(cx-w*0.05,0,cx+w*0.05,0);
      vg.addColorStop(0,'rgba(255,255,252,0)'); vg.addColorStop(0.5,'rgba(255,255,252,0.9)'); vg.addColorStop(1,'rgba(255,255,252,0)');
      ectx.fillStyle = vg; ectx.fillRect(cx-w*0.05,0,w*0.1,h);
    });
    var envTexSrc = new THREE.CanvasTexture(envCanvas);
    envTexSrc.mapping = THREE.EquirectangularReflectionMapping; envTexSrc.colorSpace = THREE.SRGBColorSpace;
    var pmrem = new THREE.PMREMGenerator(renderer);
    var envMap = pmrem.fromEquirectangular(envTexSrc).texture;
    pmrem.dispose();

    return { background: bgTex, environment: envMap };
  }

  function applyStampFromState(){
    stamp.setText(state.text);
    stamp.setFont(state.font);
    ensureStampFont();
    stamp.setRotation(state.rot);
    stamp.setSize(state.sizeAuto ? 0 : Math.min(stamp.canvas.width, stamp.canvas.height)*0.5*(state.size/100));
    stamp.setLineHeight(state.lineHeight);
    stamp.setAlign(state.align);
    if(state.mode === 'image' && state.imgFile) stamp.setImage(state.imgFile);
  }
  function redrawStamp(){ if(stamp) applyStampFromState(); }

  /* ================= fonts on demand =================
     The library has 31 faces but only Fraunces and Work Sans ship with the theme (layout/theme.liquid).
     Every other face is requested from Google Fonts the first time it is needed: when its sample row
     scrolls into view or when the customer picks it. One request per face, so one failing face
     can never block the others; the stamp is redrawn as soon as the real font is available. */
  var fontPromises = {};
  function ensureFont(f){
    var m = (f && f.css || '').match(/^(italic\s+)?(\d{3})\s+%Fpx\s+'([^']+)'/);
    if(!m) return Promise.resolve();
    var italic = !!m[1], weight = m[2], family = m[3];
    if(family === 'Fraunces' || family === 'Work Sans') return Promise.resolve();
    var key = family + '|' + italic + '|' + weight;
    if(!fontPromises[key]){
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=' + encodeURIComponent(family).replace(/%20/g, '+') +
        ':' + (italic ? 'ital,wght@1,' : 'wght@') + weight + '&display=swap';
      fontPromises[key] = new Promise(function(resolve){
        link.onload = function(){ resolve(); };
        link.onerror = function(){ resolve(); };
        document.head.appendChild(link);
      }).then(function(){
        return document.fonts && document.fonts.load ? document.fonts.load(f.css.replace('%F', '32'), 'Aa') : null;
      }).catch(function(){});
    }
    return fontPromises[key];
  }
  function ensureStampFont(){
    var f = fontById(state.font);
    ensureFont(f).then(function(){ if(stamp && fontById(state.font) === f && stamp.redraw) stamp.redraw(); });
  }

  /* ================= picker ================= */
  function renderPicker(){
    pickerGrid.innerHTML = '';
    allProducts.forEach(function(p){
      var card = document.createElement('button');
      card.type = 'button'; card.className = 'workshop-picker-card';
      if(!p.available) card.dataset.unavailable = 'true';
      if(!p.enabled){
        card.dataset.comingSoon = 'true';
        var badge = document.createElement('span');
        badge.className = 'workshop-picker-card-badge'; badge.textContent = 'Bald verfügbar';
        card.appendChild(badge);
      }
      var img = document.createElement('img');
      img.src = p.image || ''; img.alt = ''; img.loading = 'lazy';
      var body = document.createElement('div'); body.className = 'workshop-picker-card-body';
      var title = document.createElement('span'); title.className = 'workshop-picker-card-title'; title.textContent = p.title;
      var price = document.createElement('span'); price.className = 'workshop-picker-card-price'; price.textContent = money(p.price);
      body.appendChild(title); body.appendChild(price);
      card.appendChild(img); card.appendChild(body);
      card.addEventListener('click', function(){ selectProduct(p.handle); });
      pickerGrid.appendChild(card);
    });
  }

  /* ================= step navigation ================= */
  function activeSteps(){
    var steps = STEP_ORDER.slice();
    if(!state.product || state.product.variants.length <= 1){
      steps = steps.filter(function(s){ return s !== 'variant'; });
    }
    return steps;
  }
  function renderStepsNav(){
    stepsNav.innerHTML = '';
    var steps = activeSteps();
    var currentIndex = steps.indexOf(state.step);
    steps.forEach(function(s, i){
      var btn = document.createElement('button');
      btn.type = 'button'; btn.className = 'workshop-step'; btn.dataset.step = s;
      var iconName = { product:'board', variant:'tag', design:'photo', position:'target', cart:'cart' }[s];
      btn.innerHTML = ICONS[iconName] + '<span>' + (i+1) + '. ' + STEP_LABELS[s] + '</span>';
      if(s === state.step) btn.setAttribute('aria-current', 'step');
      if(i < currentIndex) btn.dataset.done = 'true';
      if(i > currentIndex && !state.product) btn.disabled = true;
      btn.addEventListener('click', function(){
        if(s === 'product'){ showPicker(); return; }
        if(!state.product) return;
        goToStep(s);
      });
      stepsNav.appendChild(btn);
    });
  }
  function goToStep(step){
    state.step = step;
    renderStepsNav();
    renderControls();
  }
  function nextStep(){
    var steps = activeSteps();
    var i = steps.indexOf(state.step);
    if(i >= 0 && i < steps.length - 1) goToStep(steps[i+1]);
  }
  function previousStep(){
    var steps = activeSteps();
    var i = steps.indexOf(state.step);
    var target = i > 0 ? steps[i-1] : 'product';
    if(target === 'product') showPicker();
    else goToStep(target);
  }
  function backArrowButton(){
    var btn = el('button', 'workshop-back-arrow', '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M19 12H5M11 6l-6 6 6 6"/></svg><span>Zurück</span>');
    btn.type = 'button';
    btn.addEventListener('click', previousStep);
    return btn;
  }

  function showPicker(){
    if(mountViewer._stop) mountViewer._stop();
    teardownViewer();
    state.product = null; state.variant = null; state.step = 'product';
    picker.hidden = false; shell.hidden = true;
    renderStepsNav();
    history.replaceState(null, '', location.pathname);
  }
  function showShell(){ picker.hidden = true; shell.hidden = false; }

  /* ================= select product / variant ================= */
  function selectProduct(handle, variantId, draft){
    var product = findProduct(handle);
    if(!product) return;
    if(!product.enabled){
      showSoonOverlay();
      showPicker();
      return;
    }
    state.product = product;
    state.variant = variantId ? findVariant(product, variantId) : product.variants[0];
    var cfg = CONFIGS[handle];
    state.surface = cfg.defaultSurface || null;
    state.mode = 'text'; state.text = randomExampleText(); state.font = 'fraunces';
    state.rot = 0; state.size = 70; state.sizeAuto = true; state.lineHeight = 1.15; state.align = 'center';
    state.imgFile = null; state.imgFileName = null; state.imgScale = 1;
    state.pos = { x: 0.5, y: 0.5 };

    if(draft && draft.handle === handle){
      if(draft.variantId) state.variant = findVariant(product, draft.variantId) || state.variant;
      state.surface = draft.surface || state.surface;
      state.mode = draft.mode || state.mode;
      state.text = draft.text || state.text;
      state.font = draft.font || state.font;
      state.rot = typeof draft.rot === 'number' ? draft.rot : state.rot;
      state.size = typeof draft.size === 'number' ? draft.size : state.size;
      state.sizeAuto = typeof draft.sizeAuto === 'boolean' ? draft.sizeAuto : state.sizeAuto;
      state.lineHeight = draft.lineHeight || state.lineHeight;
      state.align = draft.align || state.align;
      state.imgScale = draft.imgScale || state.imgScale;
      state.pos = draft.pos || state.pos;
    }

    showShell();
    mountViewer();
    updateProductTag();
    updateSummary();
    var steps = activeSteps();
    state.step = steps.indexOf('variant') >= 0 ? 'variant' : 'design';
    renderStepsNav();
    renderControls();

    var url = new URL(location.href);
    url.searchParams.set('product', handle);
    url.searchParams.set('variant', state.variant.id);
    history.replaceState(null, '', url.toString());
    saveDraft();
  }

  function updateProductTag(){
    productTagThumb.src = state.product.image || '';
    productTagTitle.textContent = state.product.title;
    productTagVariant.textContent = state.variant && state.variant.title !== 'Default Title' ? state.variant.title : '';
  }
  function updateSummary(){
    priceEl.textContent = state.variant ? money(state.variant.price) : '—';
    var available = !!(state.variant && state.variant.available);
    addToCartBtn.disabled = !available;
    addToCartLabel.textContent = available ? 'In den Warenkorb legen' : 'Ausverkauft';
  }

  /* ================= controls per step ================= */
  var ICONS = {
    board: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><rect x="3" y="7" width="18" height="12" rx="2"/><path d="M8 3v4M16 3v4"/><path d="M7 12h6"/></svg>',
    tag: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M20.6 12L12 20.6 3.4 12 3.4 3.4 12 3.4z"/><circle cx="7.6" cy="7.6" r="1.2"/></svg>',
    photo: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="8.5" cy="10" r="1.6"/><path d="M21 16l-5.5-5.5L5 21"/></svg>',
    target: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3.2"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/></svg>',
    cart: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M6 8h12l-1 12H7L6 8z"/><path d="M9 8V6a3 3 0 016 0v2"/></svg>',
    upload: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M12 15V4m0 0L7.5 8.5M12 4l4.5 4.5"/><path d="M4 15v3a2 2 0 002 2h12a2 2 0 002-2v-3"/></svg>',
    trash: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M5 7h14M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-8 0l1 13a1 1 0 001 1h6a1 1 0 001-1l1-13"/></svg>',
    swap: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 7h13l-3-3m3 3l-3 3"/><path d="M20 17H7l3 3m-3-3l3-3"/></svg>',
    'align-left': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 6h16M4 12h10M4 18h13"/></svg>',
    'align-center': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 6h16M7 12h10M5.5 18h13"/></svg>',
    'align-right': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 6h16M10 12h10M7 18h13"/></svg>'
  };

  function el(tag, cls, html){ var e = document.createElement(tag); if(cls) e.className = cls; if(html!=null) e.innerHTML = html; return e; }

  function renderControls(){
    controlsEl.innerHTML = '';
    if(!state.product) return;
    if(state.step === 'variant') renderVariantStep();
    else if(state.step === 'design') renderDesignStep();
    else if(state.step === 'position') renderPositionStep();
    else if(state.step === 'cart') renderCartStep();
  }

  function stepButton(label, onClick){
    var wrap = el('div', 'workshop-step-actions');
    var btn = el('button', 'btn btn-primary', label);
    btn.type = 'button';
    btn.addEventListener('click', onClick);
    wrap.appendChild(btn);
    return wrap;
  }

  function renderVariantStep(){
    controlsEl.innerHTML = '';
    controlsEl.appendChild(backArrowButton());
    controlsEl.appendChild(el('p', 'workshop-panel-title', 'Material wählen'));
    var group = el('div', 'workshop-variant-pills');
    state.product.variants.forEach(function(v){
      var btn = el('button', 'pill', v.title === 'Default Title' ? v.option1 : v.title);
      btn.type = 'button';
      if(!v.available) btn.disabled = true;
      if(state.variant && state.variant.id === v.id) btn.classList.add('active');
      btn.addEventListener('click', function(){
        state.variant = v;
        updateProductTag(); updateSummary(); saveDraft();
        renderVariantStep();
      });
      group.appendChild(btn);
    });
    controlsEl.appendChild(group);
    controlsEl.appendChild(stepButton('Weiter zu Text/Foto', nextStep));
  }

  function renderDesignStep(){
    controlsEl.innerHTML = '';
    controlsEl.appendChild(backArrowButton());
    var cfg = cfgFor();
    if(cfg.engraveSurfaces){
      controlsEl.appendChild(el('p', 'workshop-panel-title', 'Gravurfläche'));
      var surfaceRow = el('div', 'workshop-mode-tabs');
      var labels = { cap: 'Holzdeckel', body: 'Seitlich (Edelstahl)' };
      Object.keys(cfg.engraveSurfaces).forEach(function(key){
        var btn = el('button', 'workshop-mode-tab', labels[key] || key);
        btn.type = 'button'; btn.setAttribute('aria-pressed', String(state.surface === key));
        btn.addEventListener('click', function(){
          state.surface = key; state.pos = { x: 0.5, y: cfg.engraveSurfaces[key].y || 0.5 }; state.rot = 0;
          mountViewer(); renderDesignStep(); saveDraft();
        });
        surfaceRow.appendChild(btn);
      });
      controlsEl.appendChild(surfaceRow);
    }

    controlsEl.appendChild(el('p', 'workshop-panel-title', 'Gravurart'));
    var modeTabs = el('div', 'workshop-mode-tabs');
    ['text', 'image'].forEach(function(m){
      var btn = el('button', 'workshop-mode-tab', m === 'text' ? 'Text' : 'Eigenes Motiv');
      btn.type = 'button'; btn.setAttribute('aria-pressed', String(state.mode === m));
      btn.addEventListener('click', function(){
        state.mode = m;
        if(m === 'text') stamp.setText(state.text);
        else if(state.imgFile) stamp.setImage(state.imgFile);
        else { stamp.state.mode = 'image'; stamp.redraw(); }
        renderDesignStep(); saveDraft();
      });
      modeTabs.appendChild(btn);
    });
    controlsEl.appendChild(modeTabs);

    if(state.mode === 'text') renderTextControls(); else renderImageControls();
    controlsEl.appendChild(stepButton('Weiter zur Position', nextStep));
  }

  function renderTextControls(){
    var textarea = el('textarea');
    textarea.maxLength = 60; textarea.rows = 2; textarea.value = state.text;
    textarea.setAttribute('aria-label', 'Wunschtext');
    var count = el('div', 'workshop-char-count', state.text.length + '/60');
    textarea.addEventListener('input', function(){
      state.text = textarea.value; count.textContent = textarea.value.length + '/60';
      stamp.setText(state.text); saveDraft(); refreshFontSamples();
    });
    controlsEl.appendChild(textarea);
    controlsEl.appendChild(count);
    // Weihnachtsmodus (assets/xmas.js): Textideen zum Antippen
    if(window.AGXmas){
      controlsEl.appendChild(window.AGXmas.buildChips(60, function(text){
        textarea.value = text;
        textarea.dispatchEvent(new Event('input'));
        textarea.focus();
      }));
    }

    var grid = el('div', 'workshop-grid-2');
    var sizeRow = el('div', 'workshop-slider-row');
    sizeRow.appendChild(el('div', 'row-top', '<span>Größe</span><span id="WsSizeVal">' + state.size + '%</span>'));
    var sizeSlider = el('input'); sizeSlider.type = 'range'; sizeSlider.min = 30; sizeSlider.max = 100; sizeSlider.value = state.size;
    sizeSlider.addEventListener('input', function(){
      state.size = parseFloat(sizeSlider.value); state.sizeAuto = false;
      sizeRow.querySelector('#WsSizeVal').textContent = state.size + '%';
      stamp.setSize(Math.min(stamp.canvas.width, stamp.canvas.height)*0.5*(state.size/100));
      saveDraft();
    });
    sizeRow.appendChild(sizeSlider);
    grid.appendChild(sizeRow);

    var lhRow = el('div', 'workshop-slider-row');
    lhRow.appendChild(el('div', 'row-top', '<span>Zeilenabstand</span><span id="WsLhVal">' + state.lineHeight.toFixed(2) + '</span>'));
    var lhSlider = el('input'); lhSlider.type = 'range'; lhSlider.min = 90; lhSlider.max = 170; lhSlider.value = Math.round(state.lineHeight*100);
    lhSlider.addEventListener('input', function(){
      state.lineHeight = parseFloat(lhSlider.value)/100;
      lhRow.querySelector('#WsLhVal').textContent = state.lineHeight.toFixed(2);
      stamp.setLineHeight(state.lineHeight); saveDraft();
    });
    lhRow.appendChild(lhSlider);
    grid.appendChild(lhRow);
    controlsEl.appendChild(grid);

    var rotRow = el('div', 'workshop-slider-row');
    rotRow.style.marginTop = '4px';
    rotRow.appendChild(el('div', 'row-top', '<span>Winkel</span><span id="WsRotVal">' + Math.round(state.rot) + '°</span>'));
    var rotSlider = el('input'); rotSlider.type = 'range'; rotSlider.min = -180; rotSlider.max = 180; rotSlider.value = state.rot;
    rotSlider.addEventListener('input', function(){
      state.rot = parseFloat(rotSlider.value);
      rotRow.querySelector('#WsRotVal').textContent = Math.round(state.rot) + '°';
      stamp.setRotation(state.rot); saveDraft();
    });
    rotRow.appendChild(rotSlider);
    controlsEl.appendChild(rotRow);

    var alignRow = el('div', 'workshop-align-row');
    alignRow.style.marginTop = '10px';
    [['left','align-left'],['center','align-center'],['right','align-right']].forEach(function(pair){
      var btn = el('button', 'workshop-align-btn', ICONS[pair[1]]);
      btn.type = 'button'; btn.setAttribute('aria-pressed', String(state.align === pair[0]));
      btn.setAttribute('aria-label', pair[0]);
      btn.addEventListener('click', function(){
        state.align = pair[0]; stamp.setAlign(pair[0]);
        alignRow.querySelectorAll('.workshop-align-btn').forEach(function(b){ b.setAttribute('aria-pressed','false'); });
        btn.setAttribute('aria-pressed','true'); saveDraft();
      });
      alignRow.appendChild(btn);
    });
    controlsEl.appendChild(alignRow);

    controlsEl.appendChild(el('p', 'workshop-panel-title', 'Schriftart — 31 kuratierte Schriften'));
    var lib = el('div', 'workshop-font-library');
    var fontIO = 'IntersectionObserver' in window ? new IntersectionObserver(function(entries){
      entries.forEach(function(e){
        if(e.isIntersecting){ fontIO.unobserve(e.target); if(e.target._font) ensureFont(e.target._font); }
      });
    }, { rootMargin: '200px 0px' }) : null;
    FONT_CATS.forEach(function(cat){
      lib.appendChild(el('div', 'workshop-font-cat-label', cat.label));
      FONTS.filter(function(f){ return f.cat === cat.id; }).forEach(function(f){
        var row = el('button', 'workshop-font-row');
        row.type = 'button'; row.dataset.font = f.id;
        row.setAttribute('aria-pressed', String(f.id === state.font));
        var sample = el('span', 'workshop-font-row-sample', (state.text || 'Aa'));
        sample.style.font = f.css.replace('%F', '18');
        var meta = el('span', 'workshop-font-row-meta', f.label + (f.good === 'kurz' ? ' · kurze Namen' : ' · längere Texte'));
        row.appendChild(sample); row.appendChild(meta);
        if(fontIO) fontIO.observe(row); else ensureFont(f);
        row._font = f;
        row.addEventListener('click', function(){
          state.font = f.id; stamp.setFont(f.id); ensureStampFont();
          lib.querySelectorAll('.workshop-font-row').forEach(function(r){ r.setAttribute('aria-pressed','false'); });
          row.setAttribute('aria-pressed','true'); saveDraft();
        });
        lib.appendChild(row);
      });
    });
    controlsEl.appendChild(lib);
  }
  function refreshFontSamples(){
    controlsEl.querySelectorAll('.workshop-font-row-sample').forEach(function(s){ s.textContent = state.text || 'Aa'; });
  }

  function renderImageControls(){
    if(!state.imgFile){
      var drop = el('label', 'workshop-upload', ICONS.upload + '<span>Datei wählen — Schwarz/Weiß-Silhouette wird automatisch erzeugt</span>');
      var input = el('input'); input.type = 'file'; input.accept = 'image/jpeg,image/png';
      input.addEventListener('change', function(e){ handleImageFile(e.target.files[0]); });
      drop.appendChild(input);
      controlsEl.appendChild(drop);
    } else {
      var preview = el('div', 'workshop-upload-preview');
      var img = el('img'); img.src = state.imgPreviewUrl || '';
      var text = el('div', 'workshop-upload-preview-text', '<strong>' + escapeHtml(state.imgFileName) + '</strong>');
      var actions = el('div', 'workshop-upload-preview-actions');
      var replaceBtn = el('button', 'btn-mini', ICONS.upload + ' Ersetzen'); replaceBtn.type = 'button';
      var replaceInput = el('input'); replaceInput.type = 'file'; replaceInput.accept = 'image/jpeg,image/png'; replaceInput.style.display = 'none';
      replaceInput.addEventListener('change', function(e){ handleImageFile(e.target.files[0]); });
      replaceBtn.appendChild(replaceInput);
      replaceBtn.addEventListener('click', function(){ replaceInput.click(); });
      var removeBtn = el('button', 'btn-mini', ICONS.trash + ' Entfernen'); removeBtn.type = 'button';
      removeBtn.addEventListener('click', function(){
        state.imgFile = null; state.imgFileName = null; state.imgPreviewUrl = null;
        renderDesignStep();
      });
      actions.appendChild(replaceBtn); actions.appendChild(removeBtn);
      preview.appendChild(img); preview.appendChild(text); preview.appendChild(actions);
      controlsEl.appendChild(preview);

      var scaleRow = el('div', 'workshop-slider-row');
      scaleRow.style.marginTop = '12px';
      scaleRow.appendChild(el('div', 'row-top', '<span>Motivgröße</span><span id="WsImgScaleVal">' + Math.round(state.imgScale*100) + '%</span>'));
      var scaleSlider = el('input'); scaleSlider.type = 'range'; scaleSlider.min = 40; scaleSlider.max = 140; scaleSlider.value = Math.round(state.imgScale*100);
      scaleSlider.addEventListener('input', function(){
        state.imgScale = parseFloat(scaleSlider.value)/100;
        scaleRow.querySelector('#WsImgScaleVal').textContent = Math.round(state.imgScale*100) + '%';
        stamp.setImageScale(state.imgScale); saveDraft();
      });
      scaleRow.appendChild(scaleSlider);
      controlsEl.appendChild(scaleRow);
    }
    if(state.imgWarning) controlsEl.appendChild(el('p', 'workshop-warn', state.imgWarning));
  }
  function escapeHtml(s){ var d = document.createElement('div'); d.textContent = s || ''; return d.innerHTML; }

  function handleImageFile(file){
    state.imgWarning = null;
    if(!file) return;
    if(!/^image\/(jpeg|png)$/.test(file.type)){
      state.imgWarning = 'Bitte eine JPG- oder PNG-Datei wählen.';
      renderDesignStep();
      return;
    }
    var reader = new FileReader();
    reader.onload = function(ev){
      var img = new Image();
      img.onload = function(){
        if(Math.min(img.naturalWidth, img.naturalHeight) < 500){
          state.imgWarning = 'Für beste Gravur-Qualität empfehlen wir mind. 500×500px (dein Bild: ' + img.naturalWidth + '×' + img.naturalHeight + 'px). Du kannst trotzdem fortfahren.';
        }
        photoToSilhouette(img, function(silhouette){
          state.imgFile = silhouette; state.imgFileName = file.name; state.imgPreviewUrl = ev.target.result;
          state.mode = 'image'; stamp.setImage(silhouette); stamp.setImageScale(state.imgScale);
          renderDesignStep();
        });
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  }

  function renderPositionStep(){
    controlsEl.innerHTML = '';
    controlsEl.appendChild(backArrowButton());
    controlsEl.appendChild(el('p', 'workshop-panel-title', 'Position & Vorschau'));
    controlsEl.appendChild(el('p', null, 'Ziehe direkt am Produkt rechts, um Text oder Motiv zu verschieben. Für die Feinjustierung:'));
    var presetRow = el('div', 'workshop-align-preset-row');
    [['left','Links'],['center','Zentrieren'],['right','Rechts'],['top','Oben'],['bottom','Unten']].forEach(function(pair){
      var btn = el('button', 'btn-mini', pair[1]);
      btn.type = 'button';
      btn.addEventListener('click', function(){
        if(pair[0] === 'center'){ state.pos.x = 0.5; state.pos.y = 0.5; }
        else if(pair[0] === 'top'){ state.pos.y = 0.06; }
        else if(pair[0] === 'bottom'){ state.pos.y = 0.94; }
        else if(pair[0] === 'left'){ state.pos.x = 0.06; }
        else if(pair[0] === 'right'){ state.pos.x = 0.94; }
        if(webglOk) replaceDecal(state.pos.x, state.pos.y);
        saveDraft();
      });
      presetRow.appendChild(btn);
    });
    var resetBtn = el('button', 'btn-mini', 'Zurücksetzen');
    resetBtn.type = 'button';
    resetBtn.addEventListener('click', function(){
      state.pos = { x: 0.5, y: 0.5 }; state.rot = 0; state.size = 70; state.sizeAuto = true;
      stamp.setRotation(0); stamp.setSize(0);
      if(webglOk) replaceDecal(0.5, 0.5);
      saveDraft(); renderPositionStep();
    });
    presetRow.appendChild(resetBtn);
    controlsEl.appendChild(presetRow);
    controlsEl.appendChild(stepButton('Weiter zum Warenkorb', nextStep));
  }

  function renderCartStep(){
    controlsEl.innerHTML = '';
    controlsEl.appendChild(backArrowButton());
    controlsEl.appendChild(el('p', 'workshop-panel-title', 'Übersicht'));
    var list = el('div', null);
    list.style.cssText = 'display:flex;flex-direction:column;gap:8px;font-size:13.5px;';
    function row(label, value){
      var r = el('div'); r.style.cssText = 'display:flex;justify-content:space-between;gap:12px;';
      r.innerHTML = '<span style="color:var(--ink-soft)">' + label + '</span><strong style="text-align:right;">' + escapeHtml(value) + '</strong>';
      return r;
    }
    list.appendChild(row('Produkt', state.product.title));
    if(state.variant && state.variant.title !== 'Default Title') list.appendChild(row('Material', state.variant.title));
    if(cfgFor().engraveSurfaces) list.appendChild(row('Gravurfläche', state.surface === 'cap' ? 'Holzdeckel' : 'Seitlich (Edelstahl)'));
    if(state.mode === 'text'){
      list.appendChild(row('Text', state.text));
      list.appendChild(row('Schriftart', fontById(state.font).label));
    } else {
      list.appendChild(row('Motiv', state.imgFileName || '—'));
    }
    controlsEl.appendChild(list);

    var actionRow = el('div', 'workshop-step-actions');
    var cartLabel = el('span', null, 'In den Warenkorb legen');
    var cartBtn = el('button', 'btn btn-primary');
    cartBtn.type = 'button';
    cartBtn.appendChild(cartLabel);
    cartBtn.addEventListener('click', function(){ performAddToCart(cartBtn, cartLabel); });
    actionRow.appendChild(cartBtn);
    controlsEl.appendChild(actionRow);
  }

  /* ================= add to cart ================= */
  function validate(){
    if(!state.product || !state.variant){ return 'Bitte wähle zuerst ein Produkt.'; }
    if(state.mode === 'text' && !state.text.trim()){ return 'Bitte gib deinen Wunschtext ein.'; }
    if(state.mode === 'image' && !state.imgFile){ return 'Bitte lade ein Motiv hoch.'; }
    return null;
  }
  function buildProperties(){
    var props = {
      'Position': Math.round(state.pos.x*100) + '% / ' + Math.round(state.pos.y*100) + '%',
      'Winkel': Math.round(state.rot) + '°'
    };
    if(cfgFor().engraveSurfaces) props['Gravurfläche'] = state.surface === 'cap' ? 'Holzdeckel' : 'Seitlich (Edelstahl)';
    if(state.mode === 'text'){
      props['Personalisierung'] = state.text;
      props['Schriftart'] = fontById(state.font).label;
      props['Größe'] = (state.sizeAuto ? 70 : state.size) + '%';
    }
    return props;
  }

  function performAddToCart(triggerBtn, triggerLabel){
    formError.hidden = true;
    var err = validate();
    if(err){ formError.textContent = err; formError.hidden = false; return; }

    var buttons = [addToCartBtn, triggerBtn].filter(function(b){ return b; });
    var labels = [addToCartLabel, triggerLabel].filter(function(l){ return l; });
    var originals = labels.map(function(l){ return l.textContent; });
    buttons.forEach(function(b){ b.disabled = true; });
    labels.forEach(function(l){ l.textContent = '…'; });

    function reset(){
      labels.forEach(function(l, i){ l.textContent = originals[i]; });
      buttons.forEach(function(b){ b.disabled = !(state.variant && state.variant.available); });
    }
    function done(){
      reset();
      try { sessionStorage.removeItem(DRAFT_KEY); } catch(e){}
      if(window.AG){ window.AG.cart.refreshUI(); window.AG.cart.open(); }
    }
    function fail(message){
      reset();
      formError.textContent = message; formError.hidden = false;
    }

    var properties = buildProperties();

    if(state.mode === 'image'){
      var fd = new FormData();
      fd.append('id', state.variant.id);
      fd.append('quantity', 1);
      Object.keys(properties).forEach(function(k){ fd.append('properties[' + k + ']', properties[k]); });
      dataUrlToBlob(state.imgPreviewUrl, function(blob){
        fd.append('properties[Motiv]', blob, state.imgFileName || 'motiv.png');
        fetch('/cart/add.js', { method: 'POST', body: fd, headers: { 'Accept': 'application/json' } })
          .then(function(res){ if(!res.ok) return res.json().then(function(d){ throw new Error(d.description || 'cart_add_failed'); }); return res.json(); })
          .then(done)
          .catch(function(e){ fail(e.message); });
      });
    } else {
      window.AG.cart.add([{ id: state.variant.id, quantity: 1, properties: properties }])
        .then(done)
        .catch(function(e){ fail(e.message || 'Der Artikel konnte nicht hinzugefügt werden.'); });
    }
  }
  addToCartBtn.addEventListener('click', function(){ performAddToCart(); });

  function dataUrlToBlob(dataUrl, callback){
    fetch(dataUrl).then(function(res){ return res.blob(); }).then(callback);
  }

  swapProductBtn.addEventListener('click', showPicker);

  resetViewBtn.addEventListener('click', function(){ if(spin) spin.reset(); });

  /* ================= boot ================= */
  function boot(){
    renderPicker();
    var params = new URLSearchParams(location.search);
    var productParam = params.get('product');
    var variantParam = params.get('variant');
    var draft = loadDraft();

    if(productParam && findProduct(productParam)){
      selectProduct(productParam, variantParam, draft);
    } else if(draft && findProduct(draft.handle)){
      selectProduct(draft.handle, draft.variantId, draft);
    } else {
      showPicker();
    }
  }
  boot();
})();
